import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Loader2,
  Tag,
  Database,
  Activity,
  ListChecks,
  CheckSquare,
  Eraser
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getTagProcessingStatus,
  startTagProcessing,
  TagProcessingStatusResponse,
  TagProcessingLog,
  TagProcessingHistoryItem,
  getTagPreview,
  TagPreviewResponse,
  TagSelectionInput,
  TagSelectionInfo
} from '../../services/tagProcessing';
import ConfigSectionShell from '../common/ConfigSectionShell';

interface TagsProcessingModalProps {
  clientId: string;
  clientName: string;
  onClose: () => void;
  onCompleted?: () => void;
  inline?: boolean;
}

interface TagSelectionState {
  tagKey: string;
  selected: boolean;
  displayName: string;
}

const STATUS_TO_BADGE: Record<string, string> = {
  idle: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700'
};

const STATUS_ICON: Record<string, JSX.Element> = {
  idle: <Clock className="w-4 h-4" />,
  in_progress: <Loader2 className="w-4 h-4 animate-spin" />,
  completed: <CheckCircle2 className="w-4 h-4" />,
  error: <AlertTriangle className="w-4 h-4" />
};

const logLevelStyles: Record<string, string> = {
  info: 'text-blue-600',
  success: 'text-green-600',
  error: 'text-red-600'
};

const MAX_RANGE = 5;

const TagsProcessingModal: React.FC<TagsProcessingModalProps> = ({
  clientId,
  clientName,
  onClose,
  onCompleted,
  inline = false
}) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<TagProcessingStatusResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [preview, setPreview] = useState<TagPreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [tagSelection, setTagSelection] = useState<Record<string, TagSelectionState>>({});

  // Toma selection: single toma or range
  const [selectionMode, setSelectionMode] = useState<'single' | 'range'>('single');
  const [selectedToma, setSelectedToma] = useState<number | null>(null);
  const [rangeFrom, setRangeFrom] = useState<number | null>(null);
  const [rangeTo, setRangeTo] = useState<number | null>(null);

  const completionNotifiedRef = useRef<boolean>(false);
  const selectionInitializedRef = useRef<boolean>(false);
  const selectionDirtyRef = useRef<boolean>(false);

  // Derived: which tomas are selected
  const selectedTomas = useMemo<number[]>(() => {
    if (selectionMode === 'single') {
      return selectedToma ? [selectedToma] : [];
    }
    if (rangeFrom && rangeTo && rangeTo >= rangeFrom) {
      const count = rangeTo - rangeFrom + 1;
      if (count > MAX_RANGE) return [];
      return Array.from({ length: count }, (_, i) => rangeFrom + i);
    }
    return [];
  }, [selectionMode, selectedToma, rangeFrom, rangeTo]);

  const hasTomaSelection = selectedTomas.length > 0;

  const rangeError = useMemo(() => {
    if (selectionMode !== 'range') return null;
    if (rangeFrom && rangeTo) {
      if (rangeTo < rangeFrom) return 'La toma "Hasta" debe ser mayor o igual a "Desde"';
      if (rangeTo - rangeFrom + 1 > MAX_RANGE) return `Máximo ${MAX_RANGE} tomas por rango`;
    }
    return null;
  }, [selectionMode, rangeFrom, rangeTo]);

  // Available toma numbers sorted desc for dropdowns
  const availableTomaNumbers = useMemo(() => {
    if (!status?.availableTomas) return [];
    return [...status.availableTomas]
      .sort((a, b) => b.tomaNumber - a.tomaNumber);
  }, [status?.availableTomas]);

  const fetchStatus = useCallback(async () => {
    setError(null);
    try {
      const response = await getTagProcessingStatus(clientId);
      setStatus(response);
    } catch (err: any) {
      console.error('Error fetching tag processing status:', err);
      setError(err?.response?.data?.error || err.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const loadPreview = useCallback(async (tomaNumber?: number) => {
    setPreviewError(null);
    setPreviewLoading(true);
    try {
      const data = await getTagPreview(clientId, tomaNumber);
      setPreview(data);
    } catch (err: any) {
      console.error('Error fetching tag preview:', err);
      setPreviewError(err?.response?.data?.error || err.message || 'Error');
    } finally {
      setPreviewLoading(false);
    }
  }, [clientId]);

  // Load status on mount
  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Load preview when toma selection changes (only load for the first toma in range to show tags)
  useEffect(() => {
    if (hasTomaSelection) {
      // Use the first selected toma to get tag keys (they should be consistent across tomas)
      loadPreview(selectedTomas[0]);
    } else {
      // Clear preview when no toma is selected
      setPreview(null);
      setTagSelection({});
      selectionInitializedRef.current = false;
    }
  }, [hasTomaSelection, selectedTomas[0], loadPreview]);

  // Notify completion
  useEffect(() => {
    if (!status) return;
    if (status.status === 'completed' && !completionNotifiedRef.current) {
      completionNotifiedRef.current = true;
      onCompleted?.();
    }
  }, [status, onCompleted]);

  // Poll while processing
  useEffect(() => {
    if (!status) return undefined;
    if (status.isProcessing || status.status === 'in_progress') {
      const interval = setInterval(() => { fetchStatus(); }, 5000);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [status, fetchStatus]);

  // Stored selection from previous runs
  const storedSelection = useMemo(() => {
    if (status?.selectedTags) return status.selectedTags;
    if (preview?.selectedTags) return preview.selectedTags;
    return null;
  }, [status?.selectedTags, preview?.selectedTags]);

  const resolveSelectionEntry = useCallback(
    (tagKey: string): { selected: boolean; displayName: string } => {
      if (!storedSelection || typeof storedSelection !== 'object') {
        return { selected: false, displayName: tagKey };
      }
      const entry = (storedSelection as Record<string, TagSelectionInfo | string>)[tagKey];
      if (!entry) return { selected: false, displayName: tagKey };

      if (typeof entry === 'string') {
        return { selected: true, displayName: entry.trim() || tagKey };
      }

      const rawDisplay =
        entry.tagDisplay ||
        (entry as Record<string, unknown>).displayName ||
        (entry as Record<string, unknown>).display || '';
      const normalized = rawDisplay ? String(rawDisplay).trim() : '';
      return { selected: true, displayName: normalized || tagKey };
    },
    [storedSelection]
  );

  // Sync tag selection with preview
  useEffect(() => {
    if (!preview) return;

    setTagSelection(prev => {
      let changed = false;
      let nextState: Record<string, TagSelectionState>;

      if (!selectionInitializedRef.current || !selectionDirtyRef.current) {
        nextState = {};
        preview.tags.forEach(tag => {
          const resolved = resolveSelectionEntry(tag.key);
          nextState[tag.key] = {
            tagKey: tag.key,
            selected: resolved.selected,
            displayName: resolved.displayName
          };
        });
        selectionInitializedRef.current = true;
        selectionDirtyRef.current = false;
        changed = true;
      } else {
        nextState = { ...prev };
        const previewKeys = new Set<string>();
        preview.tags.forEach(tag => {
          previewKeys.add(tag.key);
          if (!nextState[tag.key]) {
            const resolved = resolveSelectionEntry(tag.key);
            nextState[tag.key] = {
              tagKey: tag.key,
              selected: resolved.selected,
              displayName: resolved.displayName
            };
            changed = true;
          }
        });
        Object.keys(nextState).forEach(key => {
          if (!previewKeys.has(key)) {
            delete nextState[key];
            changed = true;
          }
        });
      }

      return changed ? nextState : prev;
    });
  }, [preview, resolveSelectionEntry]);

  const handleToggleTag = useCallback((tagKey: string) => {
    selectionDirtyRef.current = true;
    setTagSelection(prev => {
      const current = prev[tagKey] || { tagKey, selected: false, displayName: tagKey };
      return { ...prev, [tagKey]: { ...current, tagKey, selected: !current.selected, displayName: current.displayName || tagKey } };
    });
  }, []);

  const handleDisplayNameChange = useCallback((tagKey: string, value: string) => {
    selectionDirtyRef.current = true;
    const sanitized = value.slice(0, 255);
    setTagSelection(prev => {
      const current = prev[tagKey] || { tagKey, selected: true, displayName: tagKey };
      return { ...prev, [tagKey]: { ...current, tagKey, displayName: sanitized } };
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!preview) return;
    selectionDirtyRef.current = true;
    setTagSelection(prev => {
      const next: Record<string, TagSelectionState> = {};
      preview.tags.forEach(tag => {
        const existing = prev[tag.key];
        next[tag.key] = { tagKey: tag.key, selected: true, displayName: existing?.displayName || tag.key };
      });
      return next;
    });
  }, [preview]);

  const handleClearSelection = useCallback(() => {
    selectionDirtyRef.current = true;
    setTagSelection(prev => {
      const next: Record<string, TagSelectionState> = {};
      Object.keys(prev).forEach(key => {
        next[key] = { ...prev[key], selected: false };
      });
      return next;
    });
  }, []);

  const handleRestoreSelection = useCallback(() => {
    selectionDirtyRef.current = false;
    selectionInitializedRef.current = false;
    setTagSelection({});
  }, []);

  const handleStartProcessing = useCallback(async () => {
    setInfoMessage(null);
    setError(null);

    const selectionPayload: TagSelectionInput[] = Object.values(tagSelection)
      .filter(item => item.selected)
      .map(item => {
        const normalized = item.displayName?.trim();
        return {
          tagKey: item.tagKey,
          tagDisplay: normalized && normalized.length > 0 ? normalized : item.tagKey
        };
      });

    if (selectionPayload.length === 0) {
      setError(t('admin.tagsProcessing.selection.messages.needSelection'));
      return;
    }

    setActionLoading(true);
    try {
      const response = await startTagProcessing(clientId, {
        force: false,
        tagsSelection: selectionPayload,
        specificTomas: selectedTomas
      });
      if (response.started) {
        if (response.needsFullResync) {
          setInfoMessage(t('admin.tagsProcessing.messages.fullResync'));
        } else {
          setInfoMessage(t('admin.tagsProcessing.messages.jobStarted'));
        }
        completionNotifiedRef.current = false;
      } else {
        setInfoMessage(response.message || t('admin.tagsProcessing.messages.noPending'));
      }
      await fetchStatus();
      selectionDirtyRef.current = false;
    } catch (err: any) {
      console.error('Error starting tag processing:', err);
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        t('admin.tagsProcessing.messages.error');
      if (err?.response?.status === 409) {
        setError(t('admin.tagsProcessing.messages.jobAlreadyRunning'));
      } else {
        setError(message);
      }
    } finally {
      setActionLoading(false);
    }
  }, [clientId, fetchStatus, selectedTomas, tagSelection, t]);

  const logs: TagProcessingLog[] = useMemo(() => {
    if (!status?.currentJob?.logs) return [];
    return [...status.currentJob.logs].sort((a, b) => {
      return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
    });
  }, [status]);

  const history: TagProcessingHistoryItem[] = useMemo(() => {
    if (!status?.history) return [];
    return [...status.history].sort((a, b) => b.tomaNumber - a.tomaNumber);
  }, [status]);

  const selectedTagsCount = useMemo(() => {
    return Object.values(tagSelection).filter(item => item.selected).length;
  }, [tagSelection]);

  const totalAvailableTags = useMemo(() => {
    if (preview?.tags) return preview.tags.length;
    return Object.keys(tagSelection).length;
  }, [preview?.tags, tagSelection]);

  const hasStoredSelection = useMemo(() => {
    if (!storedSelection || typeof storedSelection !== 'object') return false;
    return Object.keys(storedSelection).length > 0;
  }, [storedSelection]);

  const statusBadgeClass = STATUS_TO_BADGE[status?.status || 'idle'] || STATUS_TO_BADGE.idle;
  const statusIcon = STATUS_ICON[status?.status || 'idle'] || STATUS_ICON.idle;

  const summaryCards = [
    {
      label: t('admin.tagsProcessing.summary.lastProcessed'),
      value: status?.lastProcessedToma ?? 0,
      icon: <Database className="w-5 h-5 text-blue-600" />
    },
    {
      label: t('admin.tagsProcessing.summary.pending'),
      value: status?.pendingCount ?? 0,
      icon: <Clock className="w-5 h-5 text-amber-600" />
    },
    {
      label: t('admin.tagsProcessing.summary.inserted'),
      value: status?.totals?.inserted ?? 0,
      icon: <CheckCircle2 className="w-5 h-5 text-green-600" />
    },
    {
      label: t('admin.tagsProcessing.summary.updated'),
      value: status?.totals?.updated ?? 0,
      icon: <RefreshCw className="w-5 h-5 text-purple-600" />
    }
  ];

  const canProcess = hasTomaSelection && selectedTagsCount > 0 && !actionLoading && !previewLoading;

  // Badge de estado (idle/in_progress/completed/error) — en inline va en headerRight del shell.
  const statusBadge = (
    <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${statusBadgeClass}`}>
      {statusIcon}
      {t(`admin.tagsProcessing.status.${status?.status || 'idle'}`, {
        defaultValue: status?.status || 'idle'
      })}
    </span>
  );

  // Cuerpo (contenido + footer) compartido entre modal e inline.
  const body = (
    <>
      {/* Body */}
      <div className={inline ? 'space-y-6' : 'px-6 py-5 space-y-6 overflow-y-auto flex-1 min-h-0'}>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
              <Loader2 className="w-8 h-8 animate-spin mb-3" />
              <p>{t('common.loading')}</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium">{t('admin.tagsProcessing.messages.error')}</p>
                    <p className="text-sm">{error}</p>
                  </div>
                </div>
              )}

              {infoMessage && (
                <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg text-sm">
                  {infoMessage}
                </div>
              )}

              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {summaryCards.map(card => (
                  <div
                    key={card.label}
                    className="bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 flex items-center gap-3"
                  >
                    <div className="flex-shrink-0">{card.icon}</div>
                    <div>
                      <p className="text-xs uppercase tracking-wide text-gray-500">{card.label}</p>
                      <p className="text-lg font-semibold text-gray-900">{(card.value || 0).toLocaleString()}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* === STEP 1: TOMA SELECTOR (prominent) === */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <Database className="w-5 h-5 text-blue-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      Seleccioná las tomas a procesar
                    </h3>
                    <p className="text-xs text-gray-500">
                      Elegí una toma individual o un rango de hasta {MAX_RANGE} tomas
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-end gap-4">
                  {/* Mode toggle */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Modo</label>
                    <select
                      value={selectionMode}
                      onChange={(e) => {
                        const mode = e.target.value as 'single' | 'range';
                        setSelectionMode(mode);
                        setSelectedToma(null);
                        setRangeFrom(null);
                        setRangeTo(null);
                        setPreview(null);
                        setTagSelection({});
                        selectionInitializedRef.current = false;
                      }}
                      className="text-sm border border-blue-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="single">Toma individual</option>
                      <option value="range">Rango de tomas</option>
                    </select>
                  </div>

                  {selectionMode === 'single' ? (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Toma</label>
                      <select
                        value={selectedToma ?? ''}
                        onChange={(e) => {
                          const val = e.target.value ? Number(e.target.value) : null;
                          setSelectedToma(val);
                          selectionInitializedRef.current = false;
                        }}
                        className="text-sm border border-blue-300 rounded-lg px-3 py-2 bg-white min-w-[140px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Seleccionar...</option>
                        {availableTomaNumbers.map(toma => (
                          <option key={toma.tomaNumber} value={toma.tomaNumber}>
                            Toma {toma.tomaNumber} {toma.status === 'completed' ? '✓' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Desde</label>
                        <select
                          value={rangeFrom ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? Number(e.target.value) : null;
                            setRangeFrom(val);
                            selectionInitializedRef.current = false;
                          }}
                          className="text-sm border border-blue-300 rounded-lg px-3 py-2 bg-white min-w-[140px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Seleccionar...</option>
                          {availableTomaNumbers.map(toma => (
                            <option key={toma.tomaNumber} value={toma.tomaNumber}>
                              Toma {toma.tomaNumber}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Hasta</label>
                        <select
                          value={rangeTo ?? ''}
                          onChange={(e) => {
                            const val = e.target.value ? Number(e.target.value) : null;
                            setRangeTo(val);
                            selectionInitializedRef.current = false;
                          }}
                          className="text-sm border border-blue-300 rounded-lg px-3 py-2 bg-white min-w-[140px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Seleccionar...</option>
                          {availableTomaNumbers.map(toma => (
                            <option key={toma.tomaNumber} value={toma.tomaNumber}>
                              Toma {toma.tomaNumber}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {hasTomaSelection && (
                    <span className="text-xs text-blue-700 font-medium bg-blue-100 px-3 py-2 rounded-lg">
                      {selectedTomas.length === 1
                        ? `Toma ${selectedTomas[0]}`
                        : `${selectedTomas.length} tomas: ${selectedTomas[0]}–${selectedTomas[selectedTomas.length - 1]}`}
                    </span>
                  )}
                </div>

                {rangeError && (
                  <p className="text-xs text-red-600 mt-2">{rangeError}</p>
                )}
              </div>

              {/* === STEP 2: TAG SELECTION (only if tomas selected) === */}
              {hasTomaSelection && (
                <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                  <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                    <div className="flex items-start gap-3">
                      <ListChecks className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <h3 className="text-sm font-semibold text-gray-700">
                          {t('admin.tagsProcessing.selection.header')}
                        </h3>
                        <p className="text-xs text-gray-500">
                          Tags encontradas en {selectedTomas.length === 1
                            ? `la toma ${selectedTomas[0]}`
                            : `las tomas ${selectedTomas[0]}–${selectedTomas[selectedTomas.length - 1]}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span>
                        {t('admin.tagsProcessing.selection.counter', {
                          selected: selectedTagsCount,
                          total: totalAvailableTags
                        })}
                      </span>
                      {hasStoredSelection && (
                        <button
                          onClick={handleRestoreSelection}
                          className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100"
                        >
                          <Eraser className="w-3.5 h-3.5" />
                          {t('admin.tagsProcessing.selection.actions.restore')}
                        </button>
                      )}
                      <button
                        onClick={handleSelectAll}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100"
                      >
                        <CheckSquare className="w-3.5 h-3.5" />
                        {t('admin.tagsProcessing.selection.actions.selectAll')}
                      </button>
                      <button
                        onClick={handleClearSelection}
                        className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 font-medium text-gray-600 hover:bg-gray-100"
                      >
                        <X className="w-3.5 h-3.5" />
                        {t('admin.tagsProcessing.selection.actions.clear')}
                      </button>
                    </div>
                  </div>

                  {previewLoading ? (
                    <div className="flex items-center justify-center py-12 text-sm text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                      {t('admin.tagsProcessing.selection.messages.loading')}
                    </div>
                  ) : previewError ? (
                    <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      {previewError}
                    </div>
                  ) : !preview || preview.tags.length === 0 ? (
                    <p className="mt-4 text-sm text-gray-500">
                      {t('admin.tagsProcessing.selection.messages.noTags')}
                    </p>
                  ) : (
                    <div className="mt-4 border border-gray-200 rounded-lg">
                      <div className="max-h-64 overflow-y-auto">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
                            <tr>
                              <th className="w-10 px-3 py-2 text-left">&nbsp;</th>
                              <th className="px-3 py-2 text-left">
                                {t('admin.tagsProcessing.selection.table.tagKey')}
                              </th>
                              <th className="px-3 py-2 text-left">
                                {t('admin.tagsProcessing.selection.table.displayName')}
                              </th>
                              <th className="px-3 py-2 text-left">
                                {t('admin.tagsProcessing.selection.table.sampleValues')}
                              </th>
                              <th className="px-3 py-2 text-left">
                                {t('admin.tagsProcessing.selection.table.rows')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {preview.tags.map(tag => {
                              const selection = tagSelection[tag.key];
                              const isSelected = selection?.selected ?? false;
                              const displayName = selection?.displayName ?? tag.key;
                              const samples = Array.isArray(tag.sampleValues) ? tag.sampleValues : [];
                              const limitedSamples = samples.slice(0, 5);
                              const remainingSamples = samples.length - limitedSamples.length;
                              return (
                                <tr
                                  key={tag.key}
                                  className={isSelected ? 'bg-white' : 'bg-gray-50'}
                                >
                                  <td className="px-3 py-2 align-top">
                                    <input
                                      type="checkbox"
                                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                      checked={isSelected}
                                      onChange={() => handleToggleTag(tag.key)}
                                    />
                                  </td>
                                  <td className="px-3 py-2 align-top font-medium text-gray-700">
                                    {tag.key}
                                  </td>
                                  <td className="px-3 py-2 align-top">
                                    <input
                                      type="text"
                                      value={displayName}
                                      onChange={event => handleDisplayNameChange(tag.key, event.target.value)}
                                      disabled={!isSelected}
                                      placeholder={tag.key}
                                      className={`w-full rounded-lg border px-2 py-1 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                                        isSelected ? 'border-gray-300 text-gray-700' : 'border-gray-200 bg-gray-100 text-gray-400'
                                      }`}
                                    />
                                  </td>
                                  <td className="px-3 py-2 align-top text-xs text-gray-600">
                                    {limitedSamples.length > 0 ? (
                                      <div className="flex flex-wrap gap-1">
                                        {limitedSamples.map(value => (
                                          <span
                                            key={`${tag.key}-sample-${value}`}
                                            className="inline-flex max-w-[120px] truncate rounded-full bg-gray-100 px-2 py-0.5"
                                            title={value}
                                          >
                                            {value}
                                          </span>
                                        ))}
                                        {remainingSamples > 0 && (
                                          <span className="text-gray-400">+{remainingSamples}</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-gray-400">—</span>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 align-top text-xs text-gray-600">
                                    {tag.rowsWithTag.toLocaleString()}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Process status */}
              <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-blue-600" />
                    {t('admin.tagsProcessing.currentJob.header')}
                  </h3>
                  <span className="text-xs text-gray-500">
                    {status?.currentJob?.startedAt
                      ? new Date(status.currentJob.startedAt).toLocaleString()
                      : ''}
                  </span>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-xs text-gray-500">{t('admin.tagsProcessing.currentJob.currentToma')}</p>
                    <p className="text-sm font-medium text-gray-900">
                      {status?.currentJob?.currentToma
                        ? `#${status.currentJob.currentToma}`
                        : t('admin.tagsProcessing.messages.noPending')}
                    </p>
                    {status?.currentJob?.mode && (
                      <p className="text-xs text-gray-500 mt-1">
                        {status.currentJob.mode === 'full_resync'
                          ? t('admin.tagsProcessing.currentJob.modeFull')
                          : t('admin.tagsProcessing.currentJob.modeIncremental')}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>{t('admin.tagsProcessing.currentJob.progress')}</span>
                      <span>{status?.currentJob?.progressPercentage ?? 0}%</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          status?.status === 'completed' ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{ width: `${status?.currentJob?.progressPercentage ?? 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      {status?.currentJob
                        ? t('admin.tagsProcessing.currentJob.processedCounter', {
                            processed: status.currentJob.processedTomasCount,
                            total: status.currentJob.totalTomas
                          })
                        : ''}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-600 mb-2">
                      {t('admin.tagsProcessing.currentJob.logs')}
                    </p>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-gray-200">
                      {logs.length === 0 ? (
                        <p className="text-center text-xs text-gray-500 py-6">
                          {t('admin.tagsProcessing.messages.noData')}
                        </p>
                      ) : (
                        logs.slice(0, 30).map(log => (
                          <div key={log.id || `${log.timestamp}-${log.message}`} className="px-3 py-2 text-xs">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${logLevelStyles[log.level] || 'text-gray-600'}`}>
                                {log.message}
                              </span>
                              <span className="text-[10px] text-gray-400">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </span>
                            </div>
                            {(log.tags || log.inserted || log.updated) && (
                              <div className="text-[10px] text-gray-500 mt-1">
                                {log.tags ? `Tags: ${log.tags}` : ''}
                                {log.inserted ? ` · +${log.inserted}` : ''}
                                {log.updated ? ` · ${t('admin.tagsProcessing.summary.updated')}: ${log.updated}` : ''}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* History */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-2">
                  {t('admin.tagsProcessing.history.header')}
                </h3>
                <div className="border border-gray-200 rounded-xl overflow-hidden">
                  {history.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-6">
                      {t('admin.tagsProcessing.messages.noData')}
                    </p>
                  ) : (
                    <div className="max-h-60 overflow-y-auto divide-y divide-gray-200">
                      {history.map(item => (
                        <div key={`history-${item.tomaNumber}`} className="px-4 py-3 bg-white">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="text-sm font-semibold text-gray-900">Toma #{item.tomaNumber}</p>
                              <p className="text-xs text-gray-500">
                                {item.processedAt ? new Date(item.processedAt).toLocaleString() : ''}
                              </p>
                            </div>
                            <span className={`text-xs font-medium ${item.status === 'completed' ? 'text-green-600' : 'text-red-600'}`}>
                              {item.status === 'completed'
                                ? t('common.completed')
                                : t('common.error')}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs text-gray-600">
                            <div>
                              <p className="uppercase tracking-wide text-[10px] text-gray-400">
                                {t('admin.tagsProcessing.history.rows')}
                              </p>
                              <p className="font-medium text-gray-800">{item.rowsWithTags.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wide text-[10px] text-gray-400">
                                {t('admin.tagsProcessing.history.tags')}
                              </p>
                              <p className="font-medium text-gray-800">{item.tagsExtracted.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wide text-[10px] text-gray-400">
                                {t('admin.tagsProcessing.summary.inserted')}
                              </p>
                              <p className="font-medium text-gray-800">{item.inserted.toLocaleString()}</p>
                            </div>
                            <div>
                              <p className="uppercase tracking-wide text-[10px] text-gray-400">
                                {t('admin.tagsProcessing.summary.updated')}
                              </p>
                              <p className="font-medium text-gray-800">{item.updated.toLocaleString()}</p>
                            </div>
                          </div>
                          {item.errorMessage && (
                            <p className="text-xs text-red-600 mt-2">{item.errorMessage}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

      {/* Footer */}
      <div className={`flex items-center justify-between gap-3 ${inline ? 'pt-4 mt-6 border-t border-gray-200' : 'px-6 py-4 border-t border-gray-200 bg-gray-50'}`}>
        <button
          onClick={() => fetchStatus()}
          className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors"
          disabled={loading}
        >
          <RefreshCw className="w-4 h-4" />
          {t('admin.tagsProcessing.actions.refresh')}
        </button>

        <div className="flex items-center gap-2">
          {!inline && (
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {t('admin.tagsProcessing.actions.close')}
            </button>
          )}
          <button
            onClick={handleStartProcessing}
            disabled={!canProcess}
            className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {actionLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('common.loading')}
              </>
            ) : (
              <>
                <Tag className="w-4 h-4" />
                Calcular tags
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );

  // Inline: carcasa unificada (icon+title = sidebar: Tag / "Calcular Tags").
  if (inline) {
    return (
      <ConfigSectionShell icon={Tag} title="Calcular Tags" clientName={clientName} headerRight={statusBadge}>
        {body}
      </ConfigSectionShell>
    );
  }

  // Modal: card ad-hoc con header propio (uso desde ClientCard).
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-60 px-4">
      <div className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-3">
              <Tag className="w-6 h-6 text-blue-600" />
              {t('admin.tagsProcessing.title')} · {clientName}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t('admin.tagsProcessing.description')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {statusBadge}
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
              title={t('admin.tagsProcessing.actions.close')}
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>
        {body}
      </div>
    </div>
  );
};

export default TagsProcessingModal;
